// src/pages/patient2/PatientShell.jsx
import React, { useState, useMemo } from "react";

import BottomNav from "../../components/patient2/global/BottomNav";

// Screens
import HomeScreen from "./HomeScreen";
import ExploreScreen from "./ExploreScreen";
import MyHealthScreen from "./MyHealthScreen";
import SettingsScreen from "./SettingsScreen";

export default function PatientShell({
  userName = "MikDok User",

  // Home
  nextAppointment,
  recentVisits = [],
  recommendedDoctors = [],

  // Explore
  specialties, // kept for compatibility
  doctors = [],

  // MyHealth
  appointmentsUpcoming = [],
  appointmentsPast = [],
  documents = [],
  unreadMessages = 0,
  reviewsPending = [],
  reviewsRecent = [],

  // Callbacks (optional)
  onOpenDoctorProfile,
  onBookDoctor,
  onUploadDocument,
  onOpenAppointment,
  onOpenDocument,
  onOpenMessages,
  onWriteReview,

  // Settings
  onProfile,
  onPatientSetup,
  onNotifications,
  onLanguage,
  onLogout,
}) {
  const [activeTab, setActiveTab] = useState("home");

  // -------------------------------
  // 🔍 SEARCH + SPECIALTY FILTERS
  // -------------------------------
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");

  const specialtiesList = useMemo(() => {
    if (!doctors || doctors.length === 0) return [];
    const unique = [
      ...new Set(doctors.map((d) => d.specialty).filter(Boolean)),
    ];
    return unique;
  }, [doctors]);

  const filteredDoctors = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return doctors.filter((d) => {
      const matchSearch =
        !q ||
        d.name?.toLowerCase().includes(q) ||
        d.specialty?.toLowerCase().includes(q) ||
        d.locationLabel?.toLowerCase().includes(q);

      const matchSpec =
        !selectedSpecialty || d.specialty === selectedSpecialty;

      return matchSearch && matchSpec;
    });
  }, [doctors, searchTerm, selectedSpecialty]);

  // -------------------------------
  // RENDER SCREENS
  // -------------------------------
  const renderScreen = () => {
    switch (activeTab) {
      case "home":
        return (
          <HomeScreen
            userName={userName}
            nextAppointment={nextAppointment}
            recentVisits={recentVisits}
            recommendedDoctors={recommendedDoctors}
            // When user taps search/booking → go to Explore
            onSearch={() => setActiveTab("explore")}
            onBookAppointment={() => setActiveTab("explore")}
            // Upload shortcut → MyHealth
            onUploadDocument={() => setActiveTab("myhealth")}
            // Messages stays as callback (chat/inbox UI)
            onMessages={() => onOpenMessages?.()}
            // “My doctors” → Explore list
            onMyDoctors={() => setActiveTab("explore")}
            // “Shiko të gjitha” tek Vizitat → open MyHealth tab
            onSeeAllVisits={() => setActiveTab("myhealth")}
            // CLICK on a recent visit → ALSO open MyHealth tab
            // (no routes, same logic as your component-based dashboard)
            onSelectVisit={() => setActiveTab("myhealth")}
            // Recommended doctors: we still let parent open a profile if you want
            onSeeAllRecommended={() => setActiveTab("explore")}
            onOpenDoctor={onOpenDoctorProfile}
          />
        );

      case "explore":
        return (
          <ExploreScreen
            // search
            searchTerm={searchTerm}
            onChangeSearch={(v) => setSearchTerm(v)}
            onSubmitSearch={() => {}}
            // filters
            specialties={specialtiesList}
            selectedSpecialty={selectedSpecialty}
            onSelectSpecialty={(spec) => setSelectedSpecialty(spec)}
            onClearFilters={() => {
              setSearchTerm("");
              setSelectedSpecialty("");
            }}
            // doctors
            doctors={filteredDoctors}
            onOpenDoctorProfile={onOpenDoctorProfile}
            onBookDoctor={onBookDoctor}
            // map
            onOpenMap={() => alert("Map coming soon")}
          />
        );

      case "myhealth":
        return (
          <MyHealthScreen
            appointmentsUpcoming={appointmentsUpcoming}
            appointmentsPast={appointmentsPast}
            documents={documents}
            unreadMessages={unreadMessages}
            reviewsPending={reviewsPending}
            reviewsRecent={reviewsRecent}
            onOpenAppointment={onOpenAppointment}
            onUploadDocument={onUploadDocument}
            onOpenDocument={onOpenDocument}
            onOpenMessages={onOpenMessages}
            onWriteReview={onWriteReview}
          />
        );

      case "settings":
        return (
          <SettingsScreen
            onProfile={onProfile}
            onPatientSetup={onPatientSetup}
            onNotifications={onNotifications}
            onLanguage={onLanguage}
            onLogout={onLogout}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="p2-shell-root">
      <div className="p2-shell-screen">{renderScreen()}</div>
      <BottomNav active={activeTab} onChange={setActiveTab} />
    </div>
  );
}
